/**
 * RaceTrace Live: the box call for the focus car, on the track map with the pit ghost. When to
 * bring it in: the seconds until it next reaches the pit-in point (track-motion.ts
 * secondsToPitEntry), against its pit window (live-strategy.ts pitWindowFor) and the traffic it
 * would rejoin in (ghost-traffic.ts trafficAt).
 *
 * It looks at the next LOOK_LAPS chances to stop, one a lap. A chance is gone once its call is too
 * late: `callEarlyS` before the pit-in point (time for the driver to hear it, and the limiter zone
 * before the point the pit flags mark). An estimate: each car is taken to keep its pace, and under
 * a caution no traffic is worked out.
 */
import { lapAhead } from "./lap-ahead";
import type { PitWindow } from "./live-strategy";
import { clockText } from "./ghost-traffic";

/** Chances to stop looked at: this lap's and the next two. */
export const LOOK_LAPS = 3;

export interface BoxChance {
  /** The lap it would stop on (its in-lap). */
  lap: number;
  /** Passes of the pit-in point from now (0: the next one, "this lap"), whatever the lap count. */
  rel: number;
  /** Seconds until it reaches the pit-in point. */
  entryS: number;
  /** Seconds until the call must be made. */
  callS: number;
  /** Cars it would rejoin among; null when not known (a caution, or no ghost). */
  near: number | null;
  /** Inside its pit window; null with no window. */
  inWindow: boolean | null;
}

/** go: box on the chance shown · wait: a later lap rejoins clearer · late: the window's last lap, or past it · info: the window is not open. */
export type BoxTone = "go" | "wait" | "late" | "info";

export interface BoxCall {
  chances: BoxChance[];
  /** The chance the call is about. */
  pick: BoxChance;
  tone: BoxTone;
  /** "Box this lap", "Box next lap", "Box in 2 laps", "Window opens lap 52", "Traffic at rejoin". */
  head: string;
  /** "call in 23 s · pit entry 38 s · rejoins clear". */
  detail: string;
}

export interface BoxCallInput {
  /** Seconds to the next pit-in point (secondsToPitEntry) and to the line, at its pace. */
  entryS: number;
  lineS: number;
  /** Seconds a lap takes at its pace. */
  lapS: number;
  /** Laps it has completed. */
  lapsDone: number;
  window: PitWindow | null;
  callEarlyS: number;
  /** Cars it would rejoin among if it stopped t seconds from now; null when not known. */
  nearAt: (t: number) => number | null;
}

/** "this lap", "next lap", "in 2 laps": by the times it reaches the pit-in point, as a call is made. */
export const whenText = (c: BoxChance): string =>
  c.rel === 0 ? "this lap" : c.rel === 1 ? "next lap" : `in ${c.rel} laps`;
const carsText = (n: number) => `${n} car${n === 1 ? "" : "s"} at rejoin`;
const rejoinText = (c: BoxChance) =>
  c.near === null
    ? "traffic not estimated"
    : c.near === 0
      ? "rejoins clear"
      : carsText(c.near);
const timesText = (c: BoxChance) =>
  `call in ${clockText(c.callS)} · pit entry ${clockText(c.entryS)}`;

export function boxCall(x: BoxCallInput): BoxCall | null {
  if (!(x.lapS > 0) || !(x.entryS >= 0) || !(x.lineS >= 0)) return null;
  // The lap it is on is lapsDone + 1; a pit-in point it reaches only after the line is next lap's
  // (the stop's in-lap, which the pit window counts).
  const firstLap = x.lapsDone + 1 + (x.entryS > x.lineS ? 1 : 0);
  const w = x.window;
  const chances: BoxChance[] = [];
  for (let i = 0; chances.length < LOOK_LAPS && i <= LOOK_LAPS; i++) {
    const entryS = x.entryS + i * x.lapS;
    const callS = entryS - Math.max(0, x.callEarlyS);
    if (callS < 0) continue; // too late to call this one
    const lap = firstLap + i;
    chances.push({
      lap,
      rel: i,
      entryS,
      callS,
      near: x.nearAt(entryS),
      inWindow: w ? lap >= w.from && lap <= w.to : null,
    });
  }
  const first = chances[0];
  if (!first) return null;

  // Before the window opens: when it does.
  if (w && first.lap < w.from) {
    // The laps to it in brackets (lap-ahead.ts), as everywhere a lap to come is named.
    return {
      chances,
      pick: first,
      tone: "info",
      head: `Window opens lap ${lapAhead(w.from, x.lapsDone)}`,
      detail: `pit entry ${clockText(first.entryS)}`,
    };
  }
  const open = chances.filter((c) => c.inWindow !== false);
  // Past the window: the stint is as long as the class has run, or longer.
  if (!open.length) {
    return {
      chances,
      pick: first,
      tone: "late",
      head: `Box ${whenText(first)}`,
      detail: `past its window (lap ${w!.to}) · ${timesText(first)} · ${rejoinText(first)}`,
    };
  }
  const now = open[0];
  const lastLap = (c: BoxChance) => w !== null && c.lap === w.to;
  // No traffic worked out (a caution, or no ghost): the window's first chance.
  if (now.near === null) {
    return {
      chances,
      pick: now,
      tone: lastLap(now) ? "late" : "go",
      head: `Box ${whenText(now)}`,
      detail: `${lastLap(now) ? "last lap of the window · " : ""}${timesText(now)} · ${rejoinText(now)}`,
    };
  }
  const clear = open.find((c) => c.near === 0);
  if (clear === now) {
    return {
      chances,
      pick: now,
      tone: lastLap(now) ? "late" : "go",
      head: `Box ${whenText(now)}`,
      detail: `${lastLap(now) ? "last lap of the window · " : ""}${timesText(now)} · rejoins clear`,
    };
  }
  if (clear) {
    return {
      chances,
      pick: clear,
      tone: "wait",
      head: `Box ${whenText(clear)}`,
      detail: `${whenText(now)}: ${carsText(now.near)} · ${timesText(clear)} · rejoins clear`,
    };
  }
  // Traffic on every chance in the window.
  if (lastLap(now)) {
    return {
      chances,
      pick: now,
      tone: "late",
      head: `Box ${whenText(now)}`,
      detail: `last lap of the window · ${timesText(now)} · ${carsText(now.near)}`,
    };
  }
  const more = open.length - 1;
  return {
    chances,
    pick: now,
    tone: "wait",
    head: "Traffic at rejoin",
    detail: `${whenText(now)}: ${carsText(now.near)}${more ? `, and on the next ${more === 1 ? "lap" : `${more} laps`}` : ""}${w ? ` · window to lap ${lapAhead(w.to, x.lapsDone)}` : ""} · ${timesText(now)}`,
  };
}
